import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  ADMIN_EMAILS,
  hasAdminAccess,
  hasAgentAccess,
  normalizeRole,
} from "../utils/property";
import { activateVerifiedAgentProfile } from "../utils/userProfile";
import PageLoader from "../components/PageLoader";

const ADD_PROPERTY_ROLES = new Set(["admin", "agent", "broker", "property-manager"]);
const VERIFY_ACCOUNT_MESSAGE =
  "Please verify your account first, then log in again.";
const ACCESS_MESSAGE =
  "Your account does not have agent access yet. Contact Junub Homes if you need listing access.";

function canAccessProtectedRoute(profile, user) {
  const email = String(user?.email || "").trim().toLowerCase();

  if (ADMIN_EMAILS.includes(email) || hasAdminAccess(profile, email)) {
    return true;
  }

  return (
    ADD_PROPERTY_ROLES.has(normalizeRole(profile?.role)) &&
    hasAgentAccess(profile, user)
  );
}

export default function ProtectedRoute({ children }) {
  const [state, setState] = useState({
    loading: true,
    user: null,
    profile: null,
    canAccess: false,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({
          loading: false,
          user: null,
          profile: null,
          canAccess: false,
        });
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const profile = await activateVerifiedAgentProfile(
          user,
          snapshot.data() || {},
        );

        setState({
          loading: false,
          user,
          profile,
          canAccess: canAccessProtectedRoute(profile, user),
        });
      } catch (error) {
        console.error(error);
        setState({
          loading: false,
          user,
          profile: null,
          canAccess: canAccessProtectedRoute(null, user),
        });
      }
    });

    return () => unsubscribe();
  }, []);

  if (state.loading) {
    return <PageLoader label="Checking session..." />;
  }

  if (!state.user) {
    return <Navigate to="/login" replace />;
  }

  if (!state.canAccess) {
    const needsEmailVerification =
      state.user &&
      !state.user.emailVerified &&
      !hasAdminAccess(state.profile, state.user.email) &&
      !hasAgentAccess(state.profile, state.user);

    return (
      <Navigate
        to="/"
        replace
        state={{
          message: {
            tone: "error",
            text: needsEmailVerification
              ? VERIFY_ACCOUNT_MESSAGE
              : ACCESS_MESSAGE,
          },
        }}
      />
    );
  }

  return children;
}
